<template>
  <ListItemsWithTableLayout>
    <template #body>
      <HandlerAccessDenied :is-denied="isDenied">
        <TopCoefficientsPanel
          :isLoading="isLoading"
          :has-changes="hasChanges"
          @save="saveCoefficients"
        ></TopCoefficientsPanel>
        <div
          id="table-container"
          class="table-container flex flex-col"
          :class="[coefficients.length == 0 ? 'empty' : 'filled']"
        >
          <CoefficientsTable
            :coefficients="coefficients"
            :isLoading="isLoading"
            @update-coefficient="onCoefficientChange"
          ></CoefficientsTable>
        </div>
      </HandlerAccessDenied>
    </template>
  </ListItemsWithTableLayout>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useAuthStore } from "@/store/auth/login";
import {
  useCategoryCostsStore,
  CategoryCost,
} from "@/store/expenses/category_costs";

import ListItemsWithTableLayout from "@/widgets/layouts/ListItemsWithTableLayout.vue";
import HandlerAccessDenied from "@/shared/base/HandlerAccessDenied.vue";
import TopCoefficientsPanel from "@/entities/expenses/coefficients/panels/TopCoefficientsPanel.vue";
import CoefficientsTable from "@/entities/expenses/coefficients/CoefficientsTable.vue";

const authStore = useAuthStore();
const categoryCostsStore = useCategoryCostsStore();
const isLoading = ref(false);
const originalCoefficients = ref<CategoryCost[]>([]);
const coefficients = ref<CategoryCost[]>([]);

const isDenied = computed(() => {
  return authStore.isDeniedForPermissions(["expenses:edit"]);
});

const hasChanges = computed(() => {
  if (originalCoefficients.value.length !== coefficients.value.length) {
    return true;
  }
  return originalCoefficients.value.some((original, index) => {
    const current = coefficients.value[index];
    return original.level !== current.level || original.cost !== current.cost;
  });
});

onMounted(() => {
  loadCoefficients();
});

const loadCoefficients = () => {
  isLoading.value = true;
  categoryCostsStore
    .getCategoryCosts()
    .then(() => {
      coefficients.value = JSON.parse(
        JSON.stringify(categoryCostsStore.categoryCosts)
      );
      originalCoefficients.value = JSON.parse(
        JSON.stringify(categoryCostsStore.categoryCosts)
      );
    })
    .finally(() => {
      isLoading.value = false;
    });
};

const onCoefficientChange = (item: CategoryCost) => {
  const index = coefficients.value.findIndex((c) => c.level === item.level);
  if (index !== -1) {
    coefficients.value[index] = { ...item };
  }
};

const saveCoefficients = () => {
  isLoading.value = true;
  categoryCostsStore
    .updateCategoryCosts(coefficients.value)
    .then(() => {
      originalCoefficients.value = JSON.parse(
        JSON.stringify(coefficients.value)
      );
    })
    .finally(() => {
      isLoading.value = false;
    });
};
</script>

<style lang="scss" scoped>
.table-container {
  position: relative;
  top: 77px;
}
</style>
